import _ from 'lodash';
import { useEffect } from 'react';
import { createSelector } from 'reselect';
import { InterpolationMethod } from '@/services/calculationRunner.constants';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import {
  type SignalFromConditionFormulaState,
  type SignalFromConditionItem,
  type SignalFromConditionSignalMetadata,
  parseSignalFromConditionFormula,
} from '@/tools/signalFromCondition/signalFromCondition.utilities';
import { BASE_TOOL_COMMON_PROPS, DEFAULT_TOOL_ITEM_PROPS } from '@/tools/tool.constants';
import { buildToolRehydrationState, sanitizeToolItem } from '@/tools/tool.utilities';
import { type ActionsFactory, type ResettableStoreState, createStore, createStoreInstance } from '@seeqdev/utils';
import type { BackendDuration, FrontendDuration } from '@/services/systemConfiguration.types';
import type { StoredStatistic } from '@/tools/StatisticSelector.molecule';
import type { BaseToolPersistedState, ParameterDefinitions, ToolRehydratePayload } from '@/tools/tool.types';
import type { ValueWithUnitsItem } from '@/trend/ValueWithUnits.atom';

type SignalFromConditionEditPayload = ToolRehydratePayload & {
  formula?: string;
  signalMetadata?: { maxInterpolation?: BackendDuration };
};

export type SignalFromConditionState = BaseToolPersistedState & {
  inputItem: SignalFromConditionItem | undefined;
  condition: SignalFromConditionItem | undefined;
  interpolation: InterpolationMethod;
  stat: StoredStatistic;
  maximumDurationInputOverride: ValueWithUnitsItem | undefined;
  maximumDurationBoundingOverride: ValueWithUnitsItem | undefined;
  keyMethod: string | undefined;
  color: string;
  maxInterpolation: FrontendDuration | undefined;
  /** Maximum interpolation restored from the edited signal metadata, not persisted in UIConfig. */
  signalMetadata: SignalFromConditionSignalMetadata | undefined;
};

export type SignalFromConditionDehydratedState = SignalFromConditionState;

export type SignalFromConditionActions = {
  setSearchName: (name: string) => void;
  setInterpolation: (interpolation: InterpolationMethod) => void;
  setStat: (stat: StoredStatistic) => void;
  setInputOverride: (inputOverride: ValueWithUnitsItem) => void;
  setBoundingOverride: (boundingOverride: ValueWithUnitsItem) => void;
  setKeyMethod: (keyMethod: string) => void;
  setColor: (color: string) => void;
  setMaxInterpolation: (maxInterpolation: FrontendDuration | undefined) => void;
  setInputItem: (item: SignalFromConditionItem | null | undefined) => void;
  setCondition: (item: SignalFromConditionItem | null | undefined) => void;
  rehydrateForEdit: (payload: SignalFromConditionEditPayload, defaultMaximumDuration?: FrontendDuration) => void;
  reset: ResettableStoreState<SignalFromConditionState>;
};

const parameterDefinitions: ParameterDefinitions = {
  inputItem: { predicate: ['name', 'series'] },
  condition: { predicate: ['name', 'capsules'] },
};

const initialState: SignalFromConditionState = Object.freeze({
  ...BASE_TOOL_COMMON_PROPS,
  inputItem: undefined,
  condition: undefined,
  interpolation: InterpolationMethod.DISCRETE,
  stat: { key: null, timeUnits: 's' },
  maximumDurationInputOverride: undefined,
  maximumDurationBoundingOverride: undefined,
  keyMethod: undefined,
  color: '',
  maxInterpolation: undefined,
  signalMetadata: undefined,
});

export const dehydrateSignalFromConditionState = (
  state: SignalFromConditionState,
): SignalFromConditionDehydratedState => ({
  ...state,
  stat: { ...state.stat, key: state.stat.key ?? null },
});

export const signalFromConditionConfigParamsSelector = createSelector(
  [(state: SignalFromConditionState) => state],
  (state) => ({
    type: TREND_TOOLS.SIGNAL_FROM_CONDITION,
    advancedParametersCollapsed: state.advancedParametersCollapsed,
  }),
);

const signalFromConditionActionsFactory: ActionsFactory<SignalFromConditionState, SignalFromConditionActions> = (
  set,
  _get,
  resetState,
) => ({
  setSearchName: (name) =>
    set((draft) => {
      draft.name = name;
    }),
  setInterpolation: (interpolation) =>
    set((draft) => {
      draft.interpolation = interpolation;
    }),
  setStat: (stat) =>
    set((draft) => {
      draft.stat = stat;
    }),
  setInputOverride: (inputOverride) =>
    set((draft) => {
      draft.maximumDurationInputOverride = inputOverride;
    }),
  setBoundingOverride: (boundingOverride) =>
    set((draft) => {
      draft.maximumDurationBoundingOverride = boundingOverride;
    }),
  setKeyMethod: (keyMethod) =>
    set((draft) => {
      draft.keyMethod = keyMethod;
    }),
  setColor: (color) =>
    set((draft) => {
      draft.color = color;
    }),
  setMaxInterpolation: (maxInterpolation) =>
    set((draft) => {
      draft.maxInterpolation = maxInterpolation;
    }),
  setInputItem: (item) =>
    set((draft) => {
      const sanitizedItem = (sanitizeToolItem(item, DEFAULT_TOOL_ITEM_PROPS) ?? undefined) as
        | SignalFromConditionItem
        | undefined;
      if (!_.isEqual(draft.inputItem, sanitizedItem)) {
        draft.inputItem = sanitizedItem;
      }
    }),
  setCondition: (item) =>
    set((draft) => {
      const sanitizedItem = (sanitizeToolItem(item, DEFAULT_TOOL_ITEM_PROPS) ?? undefined) as
        | SignalFromConditionItem
        | undefined;
      if (!_.isEqual(draft.condition, sanitizedItem)) {
        draft.condition = sanitizedItem;
      }
    }),
  rehydrateForEdit: (payload, defaultMaximumDuration) =>
    set((draft) => {
      const rehydrationState = buildToolRehydrationState({
        payload,
        parameterDefinitions,
        currentAdvancedParametersCollapsed: initialState.advancedParametersCollapsed,
      });
      const formulaState: SignalFromConditionFormulaState = parseSignalFromConditionFormula({
        defaultMaximumDuration,
        formula: payload.formula,
        signalMaxInterpolation: payload.signalMetadata?.maxInterpolation,
      });

      Object.assign(draft, initialState, rehydrationState.config, rehydrationState.selectedParameters, formulaState, {
        originalParameters: rehydrationState.originalParameters,
      });
    }),
  reset: resetState,
});

const STORE_NAME = 'SignalFromCondition';

const [SignalFromConditionStoreProvider, useSignalFromConditionStoreInstance, useSignalFromConditionStoreSelector] =
  createStore(STORE_NAME, initialState, signalFromConditionActionsFactory);

export { SignalFromConditionStoreProvider };

export const useSignalFromConditionId = () => useSignalFromConditionStoreSelector((state) => state.id);
export const useSignalFromConditionName = () => useSignalFromConditionStoreSelector((state) => state.name);
export const useSignalFromConditionOriginalParameters = () =>
  useSignalFromConditionStoreSelector((state) => state.originalParameters);
export const useSignalFromConditionInputItem = () => useSignalFromConditionStoreSelector((state) => state.inputItem);
export const useSignalFromConditionCondition = () => useSignalFromConditionStoreSelector((state) => state.condition);
export const useSignalFromConditionInterpolation = () =>
  useSignalFromConditionStoreSelector((state) => state.interpolation);
export const useSignalFromConditionColor = () => useSignalFromConditionStoreSelector((state) => state.color);
export const useSignalFromConditionStat = () => useSignalFromConditionStoreSelector((state) => state.stat);
export const useSignalFromConditionInputOverride = () =>
  useSignalFromConditionStoreSelector((state) => state.maximumDurationInputOverride);
export const useSignalFromConditionBoundingOverride = () =>
  useSignalFromConditionStoreSelector((state) => state.maximumDurationBoundingOverride);
export const useSignalFromConditionKeyMethod = () => useSignalFromConditionStoreSelector((state) => state.keyMethod);
export const useSignalFromConditionMaxInterpolation = () =>
  useSignalFromConditionStoreSelector((state) => state.maxInterpolation);
export const useSignalFromConditionEditMaxInterpolation = () =>
  useSignalFromConditionStoreSelector((state) => state.signalMetadata?.maxInterpolation);
export const useSignalFromConditionConfigParams = () =>
  useSignalFromConditionStoreSelector(signalFromConditionConfigParamsSelector);
export const useSignalFromConditionActions = () => useSignalFromConditionStoreInstance().actions;

export function createTestSignalFromConditionStore(overrides?: Partial<SignalFromConditionState>) {
  if (process.env.NODE_ENV !== 'test') {
    throw new Error('createTestSignalFromConditionStore() should only be used in tests.');
  }
  return createStoreInstance({ ...initialState, ...overrides }, signalFromConditionActionsFactory);
}

/** @deprecated This function is ONLY for legacy migration from Flux/Baobab stores. */
export function legacyCreateSignalFromConditionStore() {
  return createStoreInstance<SignalFromConditionState, SignalFromConditionActions>(
    initialState,
    signalFromConditionActionsFactory,
    { storeName: STORE_NAME },
  );
}

export const usePersistSignalFromConditionStore = () => {
  const register = useRegisterPersistedStore();
  const store = useSignalFromConditionStoreInstance();
  useEffect(
    () =>
      register({
        storeName: 'sqSignalFromConditionStore',
        persistenceLevel: 'WORKSHEET',
        store,
        dehydrate: dehydrateSignalFromConditionState,
      }),
    [register, store],
  );
};
